import { Booking, Operator } from "./model";
import { Currency } from "../Currencies/model";

/**
 * Operator-side pricing for the booking page, computed once for every card that
 * shows it (Overview pricing, Operator-tab pricing, Discounts & revenues) so the
 * figures can't disagree.
 *
 * Commission = client charter price − net charter fee, both in the operator's
 * currency. The client charter price is the customer's discounted charter price
 * (converted when the operator bills in another currency) unless ops have typed
 * one into the operator pricing modal. Previously the commission used only that
 * typed value, which is 0 until someone edits it — so a €3,848 charter with a
 * €3,316 net fee showed a commission of −€3,316.
 *
 * Extras check: extras the customer pays HELM in advance are owed on to the
 * operator, so the operator's extras should equal them. Extras paid locally are
 * settled at the base and appear on neither side.
 */
export type OperatorPricingView = {
  currencyCode: string;
  clientPrice: number;
  clientPriceSource: "customer" | "manual";
  netCharterFee: number;
  extras: number;
  totalPayable: number;
  commission: number;
  commissionPct: number | null;
  advanceExtras: { text?: string }[];
  /** Customer's in-advance extras, in the operator's currency. */
  customerAdvanceExtras: number;
  /** customer − operator, only when they disagree beyond rounding. */
  extrasMismatch: number | null;
  converted: boolean;
};

export const resolveOperatorCurrencyCode = (booking: Booking, operator?: Operator | null) =>
  booking?.operatorTransfer?.currencyCode ||
  booking?.pricing?.yachtCurrencyCode ||
  operator?.transferDetails?.currencyCode ||
  "EUR";

export function buildOperatorPricing(
  booking: Booking,
  currencies: Currency[] | undefined,
  operatorCurrencyCode: string,
): OperatorPricingView {
  const pricing = booking?.pricing;
  const offerCode = booking?.currency?.code || "EUR";
  const from = currencies?.find((c) => c.code === offerCode);
  const to = currencies?.find((c) => c.code === operatorCurrencyCode);
  const converted = offerCode !== operatorCurrencyCode;
  // Same rate maths as calculatePrice in PricingSection.
  const toOperator = (amount: number) =>
    !converted || !from?.rate || !to?.rate ? amount : Math.round(amount * (to.rate / from.rate) * 100) / 100;

  const amenities = booking?.offerYacht?.yacht?.amenities;
  const advanceExtras = (booking?.amenitiesDetails ?? [])
    .filter((am) => am.checked && !am.included)
    .map((am) => ({ ...amenities?.find((a) => a.id === am.id), ...am }) as any)
    .filter((am) => !am.paidLocally);

  const extrasSnapshot = pricing?.extrasPriceInOperatorCurrency ?? 0;
  const saved = booking?.operatorPricing;
  const netCharterFee = saved ? Number(saved.netCharterFee) || 0 : (pricing?.operatorPayablePrice ?? 0) - extrasSnapshot;
  // Saved extras win — before, the card overwrote them with the pricing snapshot,
  // so editing extras in the modal never showed.
  const extras = saved && saved.extras != null ? Number(saved.extras) || 0 : extrasSnapshot;
  const manualClient = saved && Number(saved.clientPrice) > 0 ? Number(saved.clientPrice) : null;
  const clientPrice = manualClient ?? toOperator(pricing?.clientPrice ?? 0);
  const commission = Math.round((clientPrice - netCharterFee) * 100) / 100;

  const customerAdvanceExtras = toOperator(advanceExtras.reduce((t: number, am: any) => t + (Number(am.price) || 0), 0));
  const diff = Math.round((customerAdvanceExtras - extras) * 100) / 100;
  // Allow for conversion rounding: the larger of 1 unit or 0.5%.
  const tolerance = Math.max(1, Math.abs(customerAdvanceExtras) * 0.005);

  return {
    currencyCode: operatorCurrencyCode,
    clientPrice,
    clientPriceSource: manualClient != null ? "manual" : "customer",
    netCharterFee,
    extras,
    totalPayable: netCharterFee + extras,
    commission,
    commissionPct: clientPrice > 0 ? Math.round((commission / clientPrice) * 1000) / 10 : null,
    advanceExtras,
    customerAdvanceExtras,
    extrasMismatch: Math.abs(diff) > tolerance ? diff : null,
    converted,
  };
}
